import { z } from 'zod'
import { api } from './api'

/**
 * Dedicated SoftwareWatch (Data Platforms) API service.
 *
 * Wraps the existing `api` axios client (baseURL already carries `/api/v1`)
 * for the platform-admin consumer/key management endpoints under
 * `/platform/data-platforms`. Response shapes are validated with Zod, mirroring
 * the project convention that undeclared keys are stripped. Raw key values from
 * create/add-key are returned as-is (revealed once by the caller, never stored).
 */

// ── Schemas (co-located with the dedicated service) ────────────────────
export const swKey = z.object({
  _id: z.string(),
  consumerId: z.string(),
  label: z.string().default(''),
  keyPrefix: z.string().default(''),
  last4: z.string().default(''),
  scopes: z.array(z.string()).default([]),
  allowedIps: z.array(z.string()).default([]),
  enabled: z.boolean(),
  expiresAt: z.string().nullable().optional(),
  lastUsedAt: z.string().nullable().optional(),
  usageCount: z.number().int().default(0),
  revokedAt: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const swConsumer = z.object({
  _id: z.string(),
  name: z.string(),
  enabled: z.boolean(),
  environment: z.string().nullable().optional(),
  createdBy: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  keys: z.array(swKey).default([]),
})

export const swConsumerListResponse = z.object({ data: z.array(swConsumer) })

export const swCatalogResponse = z.object({
  contract_version: z.string().optional(),
  scope_hint: z.string().optional(),
  domains: z
    .array(
      z.object({
        name: z.string(),
        status: z.string().optional(),
        resources: z.array(z.string()).default([]),
      }),
    )
    .default([]),
  resources: z
    .array(
      z.object({
        domain: z.string(),
        resource: z.string(),
        scope: z.string().optional(),
        pii: z.boolean().optional(),
        scope_string: z.string(),
      }),
    )
    .default([]),
})

const BASE = '/platform/data-platforms'

export const softwareWatchApi = {
  /** Registry of grantable scopes (domains + resources). */
  catalog: async () => swCatalogResponse.parse((await api.get(`${BASE}/catalog`)).data),

  /** All consumers with their keys (never the key hash). */
  listConsumers: async () =>
    swConsumerListResponse.parse((await api.get(BASE)).data),

  /** Create a consumer + its first key. Returns `{ consumer, key, raw }`. */
  createConsumer: async (body) => (await api.post(BASE, body)).data,

  /** Add a key to a consumer. Returns `{ key, raw }`. */
  addKey: async (consumerId, body) =>
    (await api.post(`${BASE}/${consumerId}/keys`, body)).data,

  /** Update a consumer (name / enabled). */
  updateConsumer: async (consumerId, body) =>
    (await api.patch(`${BASE}/${consumerId}`, body)).data,

  /** Update a key (scopes / allowedIps / enabled / expiresAt / label). */
  updateKey: async (consumerId, keyId, body) =>
    (await api.patch(`${BASE}/${consumerId}/keys/${keyId}`, body)).data,

  /** Revoke a key (access stops immediately; row kept). */
  revokeKey: async (consumerId, keyId) =>
    (await api.delete(`${BASE}/${consumerId}/keys/${keyId}`)).data,

  /** Hard-delete a consumer and all its keys. */
  deleteConsumer: async (consumerId) =>
    (await api.delete(`${BASE}/${consumerId}`)).data,
}

/** Split a comma / space / newline separated list into a clean string array. */
export function parseIpList(raw) {
  return String(raw || '')
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean)
}

// ── Shared key-status helpers (used by cards, table, and details) ──────
export function isKeyActive(key, now = Date.now()) {
  if (!key) return false
  if (!key.enabled || key.revokedAt) return false
  if (key.expiresAt && new Date(key.expiresAt).getTime() <= now) return false
  return true
}

export function keyStatusLabel(key, now = Date.now()) {
  if (key.revokedAt) return 'Revoked'
  if (!key.enabled) return 'Inactive'
  if (key.expiresAt && new Date(key.expiresAt).getTime() <= now) return 'Expired'
  return 'Active'
}
